import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import { Phone, MapPin, CheckCircle2, ArrowRight, Layers, ExternalLink } from 'lucide-react';
import { companyInfo } from '../data/mock';

const PlasteringRepairs = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', postcode: '', message: '', honeypot: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return;
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name, email: formData.email, phone: formData.phone,
          properties_managed: formData.postcode,
          job_description: `[Plastering Enquiry] ${formData.message}`,
          trade_type: 'plastering',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: '/plastering-repairs'
        }),
      });
      if (response.ok) { setIsSubmitted(true); setFormData({ name: '', email: '', phone: '', postcode: '', message: '', honeypot: '' }); }
      else { setSubmitError(true); }
    } catch { setSubmitError(true); }
    finally { setIsSubmitting(false); }
  };

  const services = [
    { title: 'Patch Plaster Repairs', desc: 'Repairing damaged areas, holes, cracks and making good before decorating' },
    { title: 'Damaged Walls', desc: 'Fixing impact damage, holes from fittings, general wall damage' },
    { title: 'Damaged Ceilings', desc: 'Ceiling repairs, cracks, water damage, artex repairs' },
    { title: 'Water Damage Repairs', desc: 'Making good after leaks — plaster repairs once leak is fixed and dried' },
    { title: 'Skimming', desc: 'Skim coating over existing plaster or plasterboard where appropriate' },
    { title: 'Making Good', desc: 'Preparing walls and ceilings for decorating after repairs or alterations' }
  ];

  const benefits = [
    'Skilled plasterers with attention to detail',
    'Clean, tidy working',
    'Smooth finishes ready for decorating',
    'Repairs matched to surrounding areas',
    '£5 million public liability insurance',
    '12-month workmanship guarantee'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Plastering & Plaster Repairs Manchester | Patch Repairs | Expert Property Maintenance</title>
        <meta name="description" content="Plastering and plaster repairs in Manchester for landlords and homeowners. Patch repairs, damaged walls and ceilings, water damage, skimming. Call 07300 405045." />
        <link rel="canonical" href="https://expert-propertymaintenance.co.uk/plastering-repairs" />
      </Helmet>

      <Header variant="default" companyPhone={companyInfo.phone} />

      {/* Hero */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-20 bg-gradient-to-b from-brand-navy to-brand-navy/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Layers className="w-4 h-4" />
              Plastering Services
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Plastering & Plaster Repairs
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Professional plastering and plaster repair services for landlords, letting agents and homeowners 
              across Greater Manchester. From small patch repairs to larger making-good works.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                <a href="#contact">Request a Quote<ArrowRight className="w-5 h-5 ml-2" /></a>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy" asChild>
                <a href={`tel:${companyInfo.phone}`}><Phone className="w-5 h-5 mr-2" />{companyInfo.phone}</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-brand-navy mb-6">Professional Plaster Repairs</h2>
              <p className="text-lg text-brand-slate mb-6">
                Plaster damage is common in rental properties — holes from fittings, cracks from movement, 
                damage from tenants. Good plaster repairs are essential before decorating, otherwise problems 
                show through the paint.
              </p>
              <p className="text-brand-slate mb-6">
                Expert Property Maintenance provides plaster repair services as part of our property maintenance 
                offering. We focus primarily on repairs and making good — the work that prepares walls and ceilings 
                for decorating.
              </p>
              <p className="text-brand-slate mb-8">
                Our plasterers achieve smooth finishes that blend with surrounding areas, ready for your decorator 
                to paint over. We work cleanly and efficiently, particularly important in occupied properties.
              </p>
              <div className="space-y-3">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                    <span className="text-brand-slate">{benefit}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-4 bg-brand-grey border border-slate-200 rounded-xl">
                <p className="text-sm text-brand-slate mb-2">Our specialist plastering brand:</p>
                <a href="https://expert-plasterer.co.uk" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-brand-yellow hover:text-yellow-300 font-semibold">
                  Expert Plasterer <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>
            <div className="bg-brand-grey rounded-2xl p-8">
              <h3 className="text-xl font-bold text-brand-navy mb-6">Plastering Services</h3>
              <div className="space-y-4">
                {services.map((service) => (
                  <div key={service.title} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium text-brand-navy">{service.title}</span>
                      <p className="text-sm text-brand-slate">{service.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-brand-grey border border-brand-yellow/30 rounded-xl">
                <p className="text-sm text-brand-slate">
                  <strong>Note:</strong> We focus on repairs and making good. For specialist external rendering 
                  or specialist plaster systems, we can recommend appropriate specialists.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Common Repairs */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-brand-navy mb-4 text-center">Common Plaster Repairs</h2>
          <p className="text-lg text-brand-slate text-center mb-12 max-w-2xl mx-auto">
            Typical plastering work we handle for landlords and letting agents.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Holes from Fittings', desc: 'TV brackets, shelving, pictures — all leave holes that need proper repair' },
              { title: 'Impact Damage', desc: 'Door handle marks, furniture damage, general knocks and dents' },
              { title: 'Cracks & Settlement', desc: 'Movement cracks, corner cracks, ceiling cracks' },
              { title: 'Water Damage', desc: 'Staining and damage from leaks (once leak is fixed and area dried)' },
              { title: 'Artex Repairs', desc: 'Matching and repairing textured ceiling damage' },
              { title: 'End of Tenancy Repairs', desc: 'Making good damage before decorating and reletting' }
            ].map((item, idx) => (
              <Card key={item.title} className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-brand-navy mb-2">{item.title}</h3>
                  <p className="text-brand-slate text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-brand-navy mb-8">Related Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <a href="/painting-decorating" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Painting & Decorating</h4>
              <p className="text-sm text-brand-slate">After plastering</p>
            </a>
            <a href="/end-of-tenancy" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">End of Tenancy</h4>
              <p className="text-sm text-brand-slate">Repairs between tenancies</p>
            </a>
            <a href="/void-property-works" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Void Property Works</h4>
              <p className="text-sm text-brand-slate">Complete turnarounds</p>
            </a>
            <a href="/property-refurbishment" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Property Refurbishment</h4>
              <p className="text-sm text-brand-slate">Full renovations</p>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-16 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-6">Get a Plastering Quote</h2>
              <p className="text-slate-300 text-lg mb-8">Tell us about your plastering requirements and we will provide a quote.</p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center"><Phone className="w-6 h-6 text-brand-navy" /></div>
                  <div>
                    <div className="text-slate-400 text-sm">Call us</div>
                    <a href={`tel:${companyInfo.phone}`} className="text-white font-bold text-lg hover:text-brand-yellow">{companyInfo.phone}</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center"><MapPin className="w-6 h-6 text-brand-navy" /></div>
                  <div>
                    <div className="text-slate-400 text-sm">Covering</div>
                    <div className="text-white font-bold">Greater Manchester & Cheshire</div>
                  </div>
                </div>
              </div>
            </div>
            <Card className="bg-white border-0">
              <CardContent className="p-6 sm:p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8 text-green-600" /></div>
                    <h3 className="text-xl font-bold text-brand-navy mb-2">Enquiry Received</h3>
                    <p className="text-brand-slate">We will be in touch with a quote.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="text-xl font-bold text-brand-navy mb-4">Request a Quote</h3>
                    <input type="text" name="website" value={formData.honeypot} onChange={(e) => setFormData({...formData, honeypot: e.target.value})} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                    <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label><Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" className="h-12" /></div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label><Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@email.com" className="h-12" /></div>
                      <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Phone *</label><Input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="07xxx xxxxxx" className="h-12" /></div>
                    </div>
                    <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Postcode</label><Input value={formData.postcode} onChange={(e) => setFormData({...formData, postcode: e.target.value})} placeholder="e.g., M1 1AA" className="h-12" /></div>
                    <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Describe the Plastering Work *</label><Textarea required value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="What needs repairing? How many areas?" rows={4} /></div>
                    <Button type="submit" className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 text-lg" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Send Enquiry'}</Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© {new Date().getFullYear()} Expert Property Maintenance</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/" className="hover:text-brand-yellow">Home</a>
              <a href="/landlord-services" className="hover:text-brand-yellow">Landlord Services</a>
              <a href="/#contact" className="hover:text-brand-yellow">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      <MobileActionBar phone={companyInfo.phone} />
    </div>
  );
};

export default PlasteringRepairs;
